import apiClient from "./apiClient";
import type {
  AnomalyAlert,
  PortfolioHealth,
  AadeGatewayCapabilities,
  AadeGatewayHealth,
  ProfitForecast,
  SyncJob,
  Insight,
  Decision,
  DecisionSubmission,
} from "./aadeService";

const AADE_GATEWAY_BASE_PATH = "/api/v1/analytics/aade/gateway";

export const aadeGatewayService = {
  async getCapabilities(): Promise<AadeGatewayCapabilities> {
    const response = await apiClient.get(`${AADE_GATEWAY_BASE_PATH}/capabilities`);
    return response.data;
  },

  async getHealth(): Promise<AadeGatewayHealth> {
    const response = await apiClient.get(`${AADE_GATEWAY_BASE_PATH}/health`);
    return response.data;
  },

  async getPortfolioHealth(): Promise<PortfolioHealth> {
    const response = await apiClient.get(`${AADE_GATEWAY_BASE_PATH}/portfolio`);
    return response.data?.data?.portfolio ?? response.data;
  },

  async getAnomalyAlerts(sinceHours: number = 24): Promise<{ alerts: AnomalyAlert[] }> {
    const response = await apiClient.get(`${AADE_GATEWAY_BASE_PATH}/anomalies`, {
      params: { since_hours: sinceHours },
    });
    if (response.data?.data?.alerts) {
      return { alerts: response.data.data.alerts };
    }
    return response.data;
  },

  async submitTenantFullSnapshot(): Promise<any> {
    const response = await apiClient.post(`${AADE_GATEWAY_BASE_PATH}/sync/tenant`, {
      mode: "full_snapshot",
    });
    return response.data;
  },

  async triggerSync(syncType: "full" | "incremental" = "full"): Promise<any> {
    const response = await apiClient.post(`${AADE_GATEWAY_BASE_PATH}/sync/trigger`, {
      sync_type: syncType,
    });
    return response.data?.data ?? response.data;
  },

  async getUploadStatus(uploadId: string): Promise<any> {
    const response = await apiClient.get(`${AADE_GATEWAY_BASE_PATH}/uploads/${uploadId}/status`);
    return response.data;
  },

  async getSyncStatus(limit: number = 10): Promise<{ sync_jobs: SyncJob[] }> {
    const response = await apiClient.get(`${AADE_GATEWAY_BASE_PATH}/sync/status`, {
      params: { limit },
    });
    return response.data?.data ?? response.data;
  },

  async getLatestInsights(insightType?: string, limit: number = 20): Promise<{ upload_id?: string; insights: Insight[] }> {
    const response = await apiClient.get(`${AADE_GATEWAY_BASE_PATH}/insights/latest`, {
      params: { insight_type: insightType, limit },
    });
    return response.data?.data ?? response.data;
  },

  async getInsightsByUpload(uploadId: string): Promise<{ upload_id?: string; insights: Insight[] }> {
    const response = await apiClient.get(`${AADE_GATEWAY_BASE_PATH}/insights/${uploadId}`);
    return response.data?.data ?? response.data;
  },

  async getProfitForecast(partnerId?: string): Promise<ProfitForecast> {
    const response = await apiClient.get(`${AADE_GATEWAY_BASE_PATH}/forecast/profit`, {
      params: { partner_id: partnerId },
    });
    return response.data?.data?.forecast ?? response.data;
  },

  async getPendingDecisions(uploadId: string): Promise<{ decisions: Decision[] }> {
    const response = await apiClient.get(`${AADE_GATEWAY_BASE_PATH}/decisions/pending`, {
      params: { upload_id: uploadId },
    });
    return { decisions: response.data?.data?.decisions ?? response.data?.decisions ?? [] };
  },

  async submitDecision(submission: DecisionSubmission): Promise<any> {
    const response = await apiClient.post(`${AADE_GATEWAY_BASE_PATH}/decisions/submit`, submission);
    return response.data;
  },

  async acknowledgeInsight(insightId: string): Promise<any> {
    const response = await apiClient.post(`${AADE_GATEWAY_BASE_PATH}/insights/${insightId}/acknowledge`);
    return response.data;
  },

  async recordInsightAction(insightId: string, action: string, notes?: string): Promise<any> {
    const response = await apiClient.post(`${AADE_GATEWAY_BASE_PATH}/insights/${insightId}/action`, {
      insight_id: insightId,
      action_taken: action,
      notes,
    });
    return response.data;
  },
};

export default aadeGatewayService;
